import { useEffect, useImperativeHandle, useState } from 'react';

import {
  isAppleSupportVersion,
  isLineInAppWebView,
} from '@autopass-web/mochi-payment/utils/support';

import { PAYMENT_METHODS_INFO, paymentMethodValues } from '../../configs';
import { useLogEvent } from '../../hooks/analytics';
import { CreditCard } from './components/credit-card';
import { Methods } from './components/methods';
import { paymentMethodStorage } from './utils';

import type { PaymentMethodTypes } from '../../configs';
import type { ControlRef } from './components/credit-card';
import type { AvailMethods } from './components/methods';
import type { ReactNode, RefObject } from 'react';

export type { AvailMethods, ControlRef };

const isApplePaySupported = () => {
  return (
    isAppleSupportVersion() &&
    !!(window as unknown as { ApplePaySession: unknown }).ApplePaySession &&
    !isLineInAppWebView()
  );
};

const defaultMethods = paymentMethodValues.map((value) => ({
  value,
  available: true,
}));

export const PaymentMethods = ({
  methods = defaultMethods,
  controlRef,
  onMethodChange,
  hint,
}: {
  methods?: AvailMethods;
  onMethodChange?: (method: PaymentMethodTypes) => void;
  controlRef: RefObject<ControlRef>;
  hint?: (method: PaymentMethodTypes) => ReactNode;
}) => {
  const logEvent = useLogEvent();
  const showedMethods = methods
    .filter((method) => {
      return method.value === 'apple_pay' ? isApplePaySupported() : true;
    })
    .toSorted((a, b) => {
      return (
        PAYMENT_METHODS_INFO[a.value].order -
        PAYMENT_METHODS_INFO[b.value].order
      );
    });

  const showedMethodsKeys = showedMethods.map(({ value }) => value);

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypes>(
    () => {
      const { method } = paymentMethodStorage.get() ?? {};

      return method ?? showedMethods[0].value;
    },
  );

  const handleMethodChange = (method: PaymentMethodTypes) => {
    logEvent('paymentOptionClick', { method });
    paymentMethodStorage.set({ method });
    setSelectedMethod(method);
    onMethodChange?.(method);
  };

  const availMethods = showedMethods
    .filter(({ available }) => available)
    .map(({ value }) => value);

  if (!availMethods.includes(selectedMethod)) {
    handleMethodChange(showedMethodsKeys[0]);
  }

  useEffect(() => {
    onMethodChange?.(selectedMethod);
  }, [selectedMethod]);

  useImperativeHandle(controlRef, () => {
    if (controlRef?.current?.submit) {
      return controlRef?.current;
    }

    return {
      submit: async () => {
        if (selectedMethod !== 'credit_card') {
          return [{ paymentMethod: selectedMethod }, undefined] as const;
        }

        // it would not reach if creditCard Comp show
        return [
          {
            paymentInfo: { number: '', cvc: '', exp: '' },
            paymentMethod: 'credit_card',
          },
          undefined,
        ] as const;
      },
    };
  }, [selectedMethod]);

  return (
    <>
      <Methods
        availMethods={showedMethods}
        currentMethod={selectedMethod}
        handleMethodClick={(method) => {
          handleMethodChange(method);
        }}
        hint={hint?.(selectedMethod) ?? null}
      />
      {selectedMethod === 'credit_card' && (
        <CreditCard controlRef={controlRef} />
      )}
    </>
  );
};
